// apps/coaster/src/worker-client.js with three differences (the coaster's clock rules are the ones this page needs).
// Kept from the coaster's: `replace()` (swap a dead worker), one `fail` path for a `fatal` message or a Worker error event
// (every call is then answered { code: 'fatal' }), and `callOnce(msg, { limitMs })` whose clock starts when the WORKER says
// it has started the request (a `started` message from geometry.worker.js), not when it is posted: the worker runs one request
// at a time, and a request that carries uploaded art can wait behind another that does, so a wait counted from the post was
// charged to the request behind (logo spec D10, scrutiny F3). A request that has not started has no clock.
// The differences: (1) no start timer at all: a worker that is slow to say `ready` (a first font download) is waited for,
// the page's own clocks bound only what runs; (2) `callOnce` takes `onProgress`, so the font download's progress reaches a
// build that carries a drawing; (3) `whenReady` is not carried over: nothing on this page calls it.
// The main thread's side of the geometry worker: start, wait for ready, call, recycle.
// manifold-3d 3.5.3 leaked ~4KB of wasm heap per evaluated extrude even when every handle was
// deleted (measured 2026-09-21: ~80KB per tag); that polygon-vector leak is patched in
// packages/core/src/manifold-loader.js. A wasm heap never shrinks, so the worker is still replaced
// after `recycleAfter` meshes/exports, while idle — kept as a safety net, not re-measured after the patch. Layout requests are 2D
// only and do not count.
const MAX_TIMER_MS = 2147483647;    // setTimeout fires at once above this (and for Infinity)
const failed = (code, id) => ({ id, ok: false, error: { code } });

export function createWorkerClient({ url, recycleAfter = 300, onFatal }) {
  let worker = null, ready = null, generations = 0, reqId = 0, dead = false;
  const pending = new Map();
  function start() {
    const w = new Worker(url, { type: 'module' });
    generations = 0;
    ready = new Promise(res => { w.onceReady = res; });
    w.onmessage = (ev) => {
      const { type, id } = ev.data;
      if (type === 'ready') { w.onceReady(); return; }
      if (type === 'fatal') { fail(w, ev.data.message); return; }
      if (type === 'progress') { for (const pid of ev.data.ids) pending.get(pid)?.onProgress?.(ev.data); return; }   // one download, many waiters
      if (type === 'started') { pending.get(id)?.onStarted?.(); return; }                                            // the worker is on this request now
      const cb = pending.get(id);
      if (!cb) return;
      pending.delete(id);
      cb.resolve(ev.data);
    };
    w.onerror = (ev) => fail(w, `worker error: ${(ev && ev.message) || 'the script did not load or crashed'}`);
    w.onmessageerror = () => fail(w, 'worker error: a message could not be read');
    worker = w;
  }
  // This worker cannot work, and the page has no other: end it, answer everybody, tell the page once. Settling the
  // worker's `ready` wakes the calls that were waiting for it; they see `dead` and answer `fatal` without posting.
  function fail(w, message) {
    if (dead || w !== worker) return;
    dead = true;
    w.terminate();
    for (const [id, cb] of [...pending]) { pending.delete(id); cb.resolve(failed('fatal', id)); }
    w.onceReady();
    onFatal(message);
  }
  // Always waits for the CURRENT worker to be ready: the worker ignores messages that arrive
  // before its 'ready', so posting early would leave this promise pending forever.
  async function call(msg, { onProgress } = {}) {
    let w;
    do { w = worker; await ready; } while (w !== worker);          // the worker may be recycled while we wait
    if (dead) return failed('fatal');
    return new Promise(resolve => {
      const id = ++reqId;                                          // the sequence number the reply echoes
      pending.set(id, { resolve, onProgress });
      worker.postMessage({ ...msg, id });
    });
  }
  // One message for ONE worker — the one that is current when this is called — with an optional clock, for a request
  // the engine may never finish (uploaded art). If that worker is swapped before it is ready, nothing is posted
  // and the answer is `replaced`: the caller decides whether to start over. The clock starts when the worker reports
  // `started` for this request: neither the worker's own start nor the time spent waiting behind another request
  // counts against it. A limit that is not a number in 1..2147483647 ms (Infinity too) is no clock: it never answers `timeout` at once.
  // A clock that fires answers `timeout` and drops the request; ending the worker is the caller's decision.
  // `onStarted`: called once, when the worker says it has started this request (the page's still-building line counts from there).
  async function callOnce(msg, { limitMs, onProgress, onStarted: tell } = {}) {
    const w = worker;
    await ready;
    if (dead) return failed('fatal');
    if (w !== worker) return failed('replaced');
    return new Promise(resolve => {
      const id = ++reqId;
      let timer = null;
      let told = false;
      const onStarted = () => {
        if (!told) { told = true; if (tell) tell(); }
        if (limitMs > 0 && limitMs <= MAX_TIMER_MS && timer === null) timer = setTimeout(() => { if (pending.delete(id)) resolve(failed('timeout', id)); }, limitMs);
      };
      pending.set(id, { resolve: (reply) => { clearTimeout(timer); resolve(reply); }, onStarted, onProgress });
      w.postMessage({ ...msg, id });
    });
  }
  function recycleIfDue() {
    if (dead || generations < recycleAfter || pending.size > 0) return;
    worker.terminate();
    start();
  }
  // The wasm engine inside the worker is dead (an `engine` reply), or busy past the caller's patience: drop it and
  // start a fresh one. Requests the old worker had are answered `replaced` — the caller decides whether to ask again.
  // A call that was still waiting for the old worker to become ready was never posted, so it is not in `pending`:
  // the old worker's `ready` is settled here, which wakes that call; it then sees a different worker and waits for
  // the new one (the loop in call()) or answers `replaced` (callOnce). Without this it would wait for a `ready`
  // that can no longer come.
  function replace() {
    if (dead) return;
    const old = worker;
    old.terminate();
    for (const [id, cb] of [...pending]) { pending.delete(id); cb.resolve(failed('replaced', id)); }
    start();
    old.onceReady();
  }
  start();
  return { call, callOnce, recycleIfDue, replace, countGeneration: () => { generations++; } };
}
